import { useState, useEffect, useRef, useCallback } from 'react';

export type DeviceOrientation = 'portrait' | 'landscape-primary' | 'landscape-secondary' | 'portrait-secondary';
export type RotationAngle = 0 | 90 | -90 | 180;

interface OrientationState {
  /** Current device orientation */
  orientation: DeviceOrientation;
  /** CSS rotation angle to apply to video (0, 90, -90, 180) */
  rotationAngle: RotationAngle;
  /** Is device in landscape mode */
  isLandscape: boolean;
  /** Is device in portrait mode */
  isPortrait: boolean;
  /** Suggests rotation lock might be enabled (device physically landscape but orientation stuck in portrait) */
  rotationLockSuspected: boolean;
  /** True while an orientation change is animating (intermediate resize events suppressed) */
  isTransitioning: boolean;
}

/** Debounce delay before committing an orientation change (ms) */
const ORIENTATION_DEBOUNCE_MS = 350;

/**
 * Custom hook to detect device physical orientation and calculate video rotation.
 *
 * FaceTime-style: orientation changes are debounced so the UI only updates once
 * the device has settled into its new orientation. Intermediate resize events
 * during the rotation animation are suppressed to prevent layout thrashing
 * (the "shrink then grow" artifact).
 */
export function useDeviceOrientation(): OrientationState {
  const [orientation, setOrientation] = useState<DeviceOrientation>('portrait');
  const [rotationLockSuspected, setRotationLockSuspected] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const orientationChangingRef = useRef(false);

  const getRotationAngle = useCallback((orient: DeviceOrientation): RotationAngle => {
    switch (orient) {
      case 'portrait':
        return 0;
      case 'landscape-primary':
        return -90;
      case 'landscape-secondary':
        return 90;
      case 'portrait-secondary':
        return 180;
      default:
        return 0;
    }
  }, []);

  const detectOrientation = useCallback((): DeviceOrientation => {
    try {
      const isLandscapeNow = window.innerWidth > window.innerHeight;
      if (!isLandscapeNow) return 'portrait';

      let orient: DeviceOrientation = 'landscape-primary';
      if ('screen' in window && 'orientation' in window.screen) {
        const apiType = (window.screen.orientation as any).type;
        if (apiType === 'landscape-secondary') orient = 'landscape-secondary';
      } else if (typeof (window as any).orientation === 'number') {
        if ((window as any).orientation === -90) orient = 'landscape-secondary';
      }
      return orient;
    } catch {
      return 'portrait';
    }
  }, []);

  useEffect(() => {
    let rotationCheckTimeout: ReturnType<typeof setTimeout> | null = null;

    // Commit orientation after debounce settles
    const commitOrientation = (orient: DeviceOrientation) => {
      setOrientation(orient);
      setIsTransitioning(false);
      orientationChangingRef.current = false;
      setRotationLockSuspected(false);
      console.log('📱 [ORIENTATION] Committed:', orient);
    };

    // Schedule a debounced orientation commit
    const scheduleCommit = () => {
      const orient = detectOrientation();

      // Mark as transitioning — callers can use this to freeze layout
      if (!orientationChangingRef.current) {
        orientationChangingRef.current = true;
        setIsTransitioning(true);
      }

      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        commitOrientation(orient);
        debounceRef.current = null;
      }, ORIENTATION_DEBOUNCE_MS);
    };

    // Initial detection — commit immediately (no debounce)
    commitOrientation(detectOrientation());

    // Resize handler — debounced to suppress intermediate values
    const resizeHandler = () => {
      // If we're already in the middle of an orientation change, just
      // extend the debounce. Don't re-render with intermediate dimensions.
      if (orientationChangingRef.current) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          commitOrientation(detectOrientation());
          debounceRef.current = null;
        }, ORIENTATION_DEBOUNCE_MS);
        return;
      }

      // Check if this resize is actually an orientation change
      const newOrient = detectOrientation();
      const currentIsLandscape = orientation === 'landscape-primary' || orientation === 'landscape-secondary';
      const newIsLandscape = newOrient === 'landscape-primary' || newOrient === 'landscape-secondary';

      if (currentIsLandscape !== newIsLandscape) {
        // Orientation changed — debounce it
        scheduleCommit();
      } else {
        // Same orientation family (e.g. just a keyboard resize) — commit quickly
        commitOrientation(newOrient);
      }
    };

    // Orientationchange handler — marks the start of a rotation animation
    const orientationChangeHandler = () => {
      console.log('📱 [ORIENTATION] orientationchange event fired');
      scheduleCommit();

      // Rotation lock detection
      rotationCheckTimeout = setTimeout(() => {
        const isPhysicallyLandscape = window.innerWidth > window.innerHeight;
        if (isPhysicallyLandscape && orientation === 'portrait') {
          console.log('⚠️ [ORIENTATION] Rotation lock suspected');
          setRotationLockSuspected(true);
        }
      }, 2000);
    };

    window.addEventListener('resize', resizeHandler);
    window.addEventListener('orientationchange', orientationChangeHandler);

    return () => {
      window.removeEventListener('resize', resizeHandler);
      window.removeEventListener('orientationchange', orientationChangeHandler);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (rotationCheckTimeout) clearTimeout(rotationCheckTimeout);
    };
  }, [orientation, detectOrientation]);

  const rotationAngle = getRotationAngle(orientation);
  const isLandscape = orientation === 'landscape-primary' || orientation === 'landscape-secondary';
  const isPortrait = !isLandscape;

  return {
    orientation,
    rotationAngle,
    isLandscape,
    isPortrait,
    rotationLockSuspected,
    isTransitioning,
  };
}
